import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import AppShell from '../components/layout/AppShell';
import StreakCalendar from '../components/study/StreakCalendar';
import { studentAPI } from '../utils/api';

const ProgressPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => { loadProgress(); }, []);

  const loadProgress = async () => {
    try {
      const res = await studentAPI.getProgress();
      setData(res.data.data);
    } catch { setData(null); }
    finally { setLoading(false); }
  };

  const stats = data?.stats || {};
  const subjectBreakdown = data?.subjectBreakdown || [];
  const weakTopics = data?.weakTopics || [];
  const recentActivity = data?.recentActivity || [];

  const totalTopics = subjectBreakdown.reduce((a, s) => a + s.total, 0);
  const totalMastered = subjectBreakdown.reduce((a, s) => a + s.mastered, 0);
  const overallPct = totalTopics > 0 ? Math.round((totalMastered / totalTopics) * 100) : 0;

  const statusColor = (status) => {
    if (status === 'mastered') return { bg: '#E6F4EC', color: '#1B7F3E', label: '✓ Mastered' };
    if (status === 'in_progress') return { bg: '#FEF3C7', color: '#B45309', label: '⏳ In Progress' };
    return { bg: '#F7F8FC', color: '#64748B', label: '○ Not Started' };
  };

  return (
    <AppShell>
      <div className="topbar">
        <div style={{ fontSize: '15px', fontWeight: 800 }}>{`📊 ${t('progress.myProgress')}`}</div>
      </div>
      <div className="page-content" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px' }}><div className="spinner" style={{ margin: '0 auto' }}></div></div>
        ) : (
          <>
            {/* Overall mastery */}
            <div className="card fade-up-1" style={{ display: 'flex', alignItems: 'center', gap: '24px', padding: '24px' }}>
              <div style={{ position: 'relative', width: '90px', height: '90px', flexShrink: 0 }}>
                <svg viewBox="0 0 90 90" style={{ transform: 'rotate(-90deg)', width: '90px', height: '90px' }}>
                  <circle cx="45" cy="45" r="38" fill="none" stroke="#E2E8F0" strokeWidth="8" />
                  <circle cx="45" cy="45" r="38" fill="none" stroke={overallPct >= 80 ? '#1B7F3E' : overallPct >= 50 ? '#FF6B00' : '#E2E8F0'} strokeWidth="8"
                    strokeDasharray={`${2 * Math.PI * 38 * overallPct / 100} ${2 * Math.PI * 38}`} strokeLinecap="round" />
                </svg>
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', fontWeight: 900, color: 'var(--text)' }}>{overallPct}%</div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '18px', fontWeight: 800, marginBottom: '4px' }}>{t('progress.overallMastery')}</div>
                <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '12px' }}>{totalMastered} of {totalTopics} {t('progress.topicsMastered')}</div>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  {[
                    { val: totalMastered, lbl: t('progress.mastered'), color: '#1B7F3E', bg: '#E6F4EC' },
                    { val: subjectBreakdown.reduce((a,s) => a + s.inProgress, 0), lbl: t('progress.inProgress'), color: '#B45309', bg: '#FEF3C7' },
                    { val: stats.revisionsCompleted || 0, lbl: t('progress.revisionsDone'), color: '#1A3C6E', bg: '#E8F0FB' },
                    { val: stats.revisionsOverdue || 0, lbl: t('progress.overdue'), color: '#DC2626', bg: '#FEE2E2' },
                  ].map(s => (
                    <div key={s.lbl} style={{ background: s.bg, borderRadius: '8px', padding: '6px 12px', textAlign: 'center' }}>
                      <div style={{ fontSize: '18px', fontWeight: 900, color: s.color, lineHeight: 1 }}>{s.val}</div>
                      <div style={{ fontSize: '10px', color: s.color, fontWeight: 700, marginTop: '2px' }}>{s.lbl}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Subject breakdown */}
            {subjectBreakdown.length > 0 && (
              <div className="card fade-up-2">
                <div style={{ fontSize: '13px', fontWeight: 800, marginBottom: '14px' }}>{`📚 ${t('progress.subjectProgress')}`}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {subjectBreakdown.map(s => {
                    const pct = s.total > 0 ? Math.round((s.mastered / s.total) * 100) : 0;
                    return (
                      <div key={s.id} onClick={() => navigate(`/subjects/${s.id}`)} style={{ cursor: 'pointer' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '16px' }}>{s.icon}</span>
                            <span style={{ fontSize: '13px', fontWeight: 700 }}>{s.name}</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px', color: 'var(--muted)' }}>
                            <span style={{ fontWeight: 700, color: '#1B7F3E' }}>{s.mastered} mastered</span>
                            <span>·</span>
                            <span>{s.total} total</span>
                            <span style={{ fontWeight: 800, color: pct >= 80 ? '#1B7F3E' : 'var(--brand-primary)' }}>{pct}%</span>
                          </div>
                        </div>
                        <div style={{ background: 'var(--border)', borderRadius: '10px', height: '6px' }}>
                          <div style={{ background: pct >= 80 ? '#1B7F3E' : 'var(--brand-primary)', borderRadius: '10px', height: '100%', width: `${pct}%`, transition: 'width 0.5s' }}></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Weak topics */}
            {weakTopics.length > 0 && (
              <div className="card fade-up-3">
                <div style={{ fontSize: '13px', fontWeight: 800, marginBottom: '14px' }}>{`🎯 ${t('progress.weakTopics')}`}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {weakTopics.map(topic => (
                    <div key={topic.topic_id} onClick={() => navigate(`/study/${topic.topic_id}`)}
                      style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', background: 'var(--bg)', borderRadius: '8px', cursor: 'pointer', border: '1px solid var(--border)' }}>
                      <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: '#FEE2E2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 900, color: '#DC2626', flexShrink: 0 }}>{topic.mastery_score}%</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '12px', fontWeight: 700 }}>{topic.topic_name}</div>
                        <div style={{ fontSize: '10px', color: 'var(--muted)', textTransform: 'capitalize' }}>{topic.subject}</div>
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--brand-primary)', fontWeight: 700 }}>{t('progress.revise')} →</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent activity */}
            {recentActivity.length > 0 && (
              <div className="card fade-up-4">
                <div style={{ fontSize: '13px', fontWeight: 800, marginBottom: '14px' }}>{`🕐 ${t('progress.recentActivity')}`}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {recentActivity.map(topic => {
                    const s = statusColor(topic.status);
                    return (
                      <div key={topic.topic_id} onClick={() => navigate(`/study/${topic.topic_id}`)}
                        style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', background: 'var(--bg)', borderRadius: '8px', cursor: 'pointer', border: '1px solid var(--border)' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '12px', fontWeight: 700 }}>{topic.topic_name}</div>
                          <div style={{ fontSize: '10px', color: 'var(--muted)', textTransform: 'capitalize' }}>{topic.subject}</div>
                        </div>
                        <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '20px', background: s.bg, color: s.color, whiteSpace: 'nowrap' }}>{s.label}</span>
                        {topic.mastery_score > 0 && <span style={{ fontSize: '11px', fontWeight: 800, color: 'var(--muted)' }}>{topic.mastery_score}%</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Empty state */}
            {subjectBreakdown.length === 0 && (
              <div className="card fade-up-2" style={{ textAlign: 'center', padding: '48px 32px' }}>
                <div style={{ fontSize: '48px', marginBottom: '12px' }}>📚</div>
                <div style={{ fontSize: '15px', fontWeight: 800, marginBottom: '8px' }}>{t('progress.noProgress')}</div>
                <p style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '20px' }}>Start studying topics and taking quizzes to see your progress here.</p>
                <button className="btn-primary" onClick={() => navigate('/subjects')}>Start Studying</button>
              </div>
            )}
          </>
        )}
      </div>
      <StreakCalendar />
    </AppShell>
  );
};

export default ProgressPage;
